import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material';
import axios from 'axios';
import React, { useEffect, useRef, useState } from 'react';
import referralService from '../../../services/referral';

/** Matches the API, which refuses a placeholder reason. */
export const MIN_DISCARD_REASON_LENGTH = 10;

/**
 * Matches the API's `@MaxLength(255)`. Checked here rather than left to the server, which
 * refuses a longer reason on every attempt. Not enforced with `maxLength`: a browser cuts a
 * pasted reason at the cap silently, and on the field that later tells a duplicate from a
 * lost referral, losing the end of it unseen is worse than asking the operator to shorten it.
 */
export const MAX_DISCARD_REASON_LENGTH = 255;

/**
 * What to tell the operator when a discard fails.
 *
 * Narrowed with `axios.isAxiosError`, the repo's convention, rather than an inline cast: a
 * non-axios throw would otherwise fall through to the generic message indistinguishably from
 * a real server error. A 409 is worded as "already discarded" because that is what it means
 * and it is not a thing to retry.
 */
export const discardErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    if (error.response?.status === 409) {
      return 'This referral was already discarded.';
    }

    // Only a 4xx message is the API talking to the operator ("reason too short"). A 5xx body
    // is whatever broke — a stack fragment, an upstream error — and is not for display.
    const status = error.response?.status ?? 0;
    const apiMessage = (error.response?.data as { message?: unknown } | undefined)?.message;
    if (status >= 400 && status < 500) {
      // Nest's ValidationPipe answers with an array of messages, one per failed constraint.
      // Reading only a string fell through to "Please try again" for exactly the failures a
      // retry can never fix.
      const text = Array.isArray(apiMessage)
        ? apiMessage.filter((part) => typeof part === 'string').join('. ')
        : typeof apiMessage === 'string'
          ? apiMessage
          : '';
      if (text.trim()) {
        return text;
      }
    }
  }

  return 'Could not discard the referral. Please try again.';
};

interface DiscardReferralDialogProps {
  referralId: number | null;
  onClose: () => void;
  onDiscarded: () => void;
}

/**
 * Asks why before discarding.
 *
 * The reason is not paperwork: a discarded referral is a patient who was referred and then
 * disappeared from the dashboard, and this field is the only thing that later separates a
 * duplicate cleaned up from a real referral lost.
 *
 * Mount it with `key={referralId}` so every open starts from a clean slate. It used to be a
 * single always-mounted instance: dismissing it mid-submit and then having the request fail
 * set the error on the hidden dialog, and the next referral opened showed that stale error.
 */
const DiscardReferralDialog: React.FC<DiscardReferralDialogProps> = ({
  referralId,
  onClose,
  onDiscarded,
}) => {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // A response arriving after the dialog was closed or remounted must not write into it.
  const live = useRef(true);
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);

  const close = () => {
    // Not dismissible while a request is in flight: closing then would leave the operator
    // with no way to learn whether the discard happened.
    if (submitting) {
      return;
    }
    onClose();
  };

  const submit = async () => {
    if (referralId === null) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await referralService.discardReferral(referralId, reason.trim());
      if (live.current) {
        onDiscarded();
      }
    } catch (caught) {
      // Surfaced rather than logged. A discard that silently did nothing leaves the operator
      // believing the referral is gone when it is still in everyone's work queue.
      if (live.current) {
        setError(discardErrorMessage(caught));
      }
    } finally {
      if (live.current) {
        setSubmitting(false);
      }
    }
  };

  const trimmedLength = reason.trim().length;
  const tooShort = trimmedLength < MIN_DISCARD_REASON_LENGTH;
  const overBy = trimmedLength - MAX_DISCARD_REASON_LENGTH;
  const tooLong = overBy > 0;

  return (
    <Dialog open={referralId !== null} onClose={close} maxWidth='sm' fullWidth>
      <DialogTitle>Discard this referral?</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          It is never deleted: the date and the reason are recorded with it. Nothing is cancelled
          in LeadSquared or Core, so if a lead is already being worked, that still needs handling
          separately.
        </DialogContentText>
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={2}
          label='Reason'
          placeholder='e.g. Duplicate of referral 8891 — same patient, same clinic, same day'
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          error={(reason.length > 0 && tooShort) || tooLong}
          helperText={
            // The reason is shown to everyone who opens the referral, so it asks for the
            // operational why, not clinical detail. The steer stays with the length error: a
            // long reason is exactly when someone is writing a clinical narrative.
            tooLong
              ? `Too long by ${overBy} character${overBy === 1 ? '' : 's'} — the limit is ${MAX_DISCARD_REASON_LENGTH}. Say why it is being dropped, not clinical detail. ${trimmedLength}/${MAX_DISCARD_REASON_LENGTH}`
              : reason.length > 0 && tooShort
                ? `Give a reason someone can act on later (at least ${MIN_DISCARD_REASON_LENGTH} characters)`
                : `Why it is being dropped, not clinical detail — everyone who opens this referral sees it. ${trimmedLength}/${MAX_DISCARD_REASON_LENGTH}`
          }
        />
        {error && (
          <Alert severity='error' sx={{ mt: 1 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={submitting}>
          Cancel
        </Button>
        <Button
          color='error'
          variant='contained'
          onClick={submit}
          disabled={tooShort || tooLong || submitting}
          startIcon={submitting ? <CircularProgress size={16} color='inherit' /> : undefined}>
          {submitting ? 'Discarding…' : 'Discard referral'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DiscardReferralDialog;
